import { useEffect } from 'react';
import { message } from 'antd';
import { named } from '../inward/scanNames';
import {
  heldHandScans,
  onHeldHandScan,
  onScreenTakesScans,
  takeUnheardDrops,
} from './useHandScanner';

const KEY = 'held-hand-scan';
/** A "was not used" line stays this long once a screen takes scans again (until then it stays up). */
export const DROPPED_NOTICE_MS = 6000;

/** The line while a read waits for a screen that takes scans: it is kept, never "scan again" (that would count twice). */
export const heldScanText = (code: string) =>
  `Still loading. ${named(code)} is kept and used once this screen is ready.`;
/** The line once a kept read was used by no screen: nothing was done with it. */
export const droppedScanText = (code: string) =>
  `${named(code)} was not used. Nothing saved. Scan it again where it is needed.`;

/**
 * A hand-scanner read that came while no screen took scans (a page still loading, or a menu) is kept for the next one
 * (useHandScanner), and said: "Still loading. BO-B01 is kept…" until a screen takes it, or "… was not used. Nothing
 * saved." once none did (round 39, Kavya: a shelf read while Move Stock loaded vanished with no word). The "not used"
 * line stays up while no screen takes scans, and DROPPED_NOTICE_MS more once one does: a slow page never lets it come
 * and go unseen (round 40, Ravi: it came and went while Quick Pick still said "Loading…"). A read dropped before Hub
 * was on screen (kept by the boot screen) is said when this comes up.
 */
export const HeldScanNotice: React.FC = () => {
  useEffect(() => {
    let dropShown = false;
    let dropTimer: ReturnType<typeof setTimeout> | undefined;
    const clearDropTimer = () => {
      if (dropTimer) clearTimeout(dropTimer);
      dropTimer = undefined;
    };
    const show = (code: string) => {
      clearDropTimer();
      dropShown = false;
      message.info({ key: KEY, content: heldScanText(code), duration: 0 });
    };
    const showDropped = (code: string) => {
      clearDropTimer();
      dropShown = true;
      message.warning({ key: KEY, content: droppedScanText(code), duration: 0 });
    };
    const unheard = takeUnheardDrops();
    const kept = heldHandScans();
    if (kept.length > 0) show(kept[kept.length - 1]);
    else if (unheard.length > 0) showDropped(unheard[unheard.length - 1]);
    const off = onHeldHandScan(({ kind, code }) => {
      if (kind === 'held') show(code);
      else if (kind === 'used') {
        if (heldHandScans().length === 0 && !dropShown) message.destroy(KEY);
      } else showDropped(code);
    });
    const offTaken = onScreenTakesScans(() => {
      if (!dropShown || dropTimer) return;
      dropTimer = setTimeout(() => {
        dropTimer = undefined;
        dropShown = false;
        message.destroy(KEY);
      }, DROPPED_NOTICE_MS);
    });
    return () => {
      off();
      offTaken();
      clearDropTimer();
      message.destroy(KEY);
    };
  }, []);
  return null;
};
